import { Transaction } from '../types';

/**
 * Sample / demo portfolio data.
 *
 * Shown on /app ONLY when a user has zero real transactions and hasn't dismissed
 * the sample banner (see `useSampleMode`). These transactions are display-only:
 * they are NEVER written to `mycryptoplanner_transactions` and never mutated.
 * They flow through the exact same `computeHoldings` / `computePortfolioStats` /
 * `buildPortfolioHistory` pipeline as real transactions, so every portfolio
 * component renders them without knowing sample mode exists. Live prices still
 * come from CoinGecko, so P&L stays realistic.
 *
 * Holdings (avg buy prices verified against the totals) — tuned so the demo
 * portfolio is strongly positive overall, with 4 winners and 1 small loser:
 *   BTC 0.15 @ ~$58,000 · ETH 2.5 @ ~$1,550 · XRP 1,500 @ $0.78 · SOL 12 @ $62 (up)
 *   ADA 800 @ $0.31 (small loss)
 */

/** ISO (YYYY-MM-DD) date `days` before today — keeps the demo history recent. */
function isoDaysAgo(days: number): string {
  const d = new Date();
  d.setDate(d.getDate() - days);
  return d.toISOString().split('T')[0];
}

interface SampleBuy {
  id: string;
  coinId: string;
  symbol: string;
  name: string;
  amount: number;
  price: number;
  wallet: string;
  daysAgo: number;
}

// Dates spread across the last ~4 months so the portfolio chart has shape.
const SAMPLE_BUYS: SampleBuy[] = [
  { id: 'sample-btc-binance', coinId: 'bitcoin',  symbol: 'BTC', name: 'Bitcoin',  amount: 0.08, price: 55000,  wallet: 'Binance', daysAgo: 122 },
  { id: 'sample-eth-binance', coinId: 'ethereum', symbol: 'ETH', name: 'Ethereum', amount: 1.5,  price: 1450,   wallet: 'Binance', daysAgo: 118 },
  { id: 'sample-btc-ledger',  coinId: 'bitcoin',  symbol: 'BTC', name: 'Bitcoin',  amount: 0.07, price: 61500,  wallet: 'Ledger',  daysAgo: 91  },
  { id: 'sample-eth-ledger',  coinId: 'ethereum', symbol: 'ETH', name: 'Ethereum', amount: 1.0,  price: 1700,   wallet: 'Ledger',  daysAgo: 61  },
  { id: 'sample-xrp-binance', coinId: 'ripple',   symbol: 'XRP', name: 'XRP',      amount: 1500, price: 0.78,   wallet: 'Binance', daysAgo: 58  },
  { id: 'sample-ada-binance', coinId: 'cardano',  symbol: 'ADA', name: 'Cardano',  amount: 800,  price: 0.31,   wallet: 'Binance', daysAgo: 42  },
  { id: 'sample-sol-binance', coinId: 'solana',   symbol: 'SOL', name: 'Solana',   amount: 12,   price: 62,     wallet: 'Binance', daysAgo: 30  },
];

/** Fully-formed sample transactions, sorted oldest → newest like the real store. */
export const SAMPLE_TRANSACTIONS: Transaction[] = SAMPLE_BUYS
  .map((b): Transaction => {
    const date = isoDaysAgo(b.daysAgo);
    return {
      id: b.id,
      coinId: b.coinId,
      symbol: b.symbol,
      name: b.name,
      type: 'BUY',
      amount: b.amount,
      price: b.price,
      date,
      timestamp: new Date(date).getTime(),
      wallet: b.wallet,
    };
  })
  .sort((a, b) => a.timestamp - b.timestamp);

/** Unique coin IDs in the sample set — fed to the price fetchers in sample mode. */
export const SAMPLE_COIN_IDS: string[] = Array.from(
  new Set(SAMPLE_TRANSACTIONS.map((t) => t.coinId))
);
